import { FC } from 'react';
import { Checkbox, Col, InputNumber, Row } from 'antd';
import { TbCalendarEvent } from 'react-icons/tb';

import PlatformDatePicker from '@/shared/components/forms/PlatformDatePicker';
import Form from '@/shared/components/forms/PlatformForm';
import PlatformSelect from '@/shared/components/forms/PlatformSelect';
import { dayjs } from '@/utilities';

interface MetadataProps {
    filters: Record<string, unknown>;
    handleChange: (path: string[], value: unknown) => void;
}

const numericValue = (value: unknown) => (typeof value === 'number' ? value : null);
const stringValue = (value: unknown) => (typeof value === 'string' ? value : '');
const stringArray = (value: unknown) => (Array.isArray(value) ? value.map(String) : []);

const Metadata: FC<MetadataProps> = ({ filters, handleChange }): JSX.Element => (
    <Form>
        <Form.Item label='Page Count' style={{ marginBottom: '0.75rem' }}>
            <Row gutter={[8, 0]}>
                <Col span={12}>
                    <InputNumber
                        aria-label='Minimum page count'
                        min={0}
                        onChange={(value) => handleChange(['minPages'], value ?? undefined)}
                        placeholder='Minimum...'
                        style={{ width: '100%' }}
                        value={numericValue(filters.minPages)}
                    />
                </Col>
                <Col span={12}>
                    <InputNumber
                        aria-label='Maximum page count'
                        min={0}
                        onChange={(value) => handleChange(['maxPages'], value ?? undefined)}
                        placeholder='Maximum...'
                        style={{ width: '100%' }}
                        value={numericValue(filters.maxPages)}
                    />
                </Col>
            </Row>
        </Form.Item>
        <Form.Item label='Word Count' style={{ marginBottom: '0.75rem' }}>
            <Row gutter={[8, 0]}>
                <Col span={12}>
                    <InputNumber
                        aria-label='Minimum word count'
                        min={0}
                        onChange={(value) => handleChange(['minWords'], value ?? undefined)}
                        placeholder='Minimum...'
                        style={{ width: '100%' }}
                        value={numericValue(filters.minWords)}
                    />
                </Col>
                <Col span={12}>
                    <InputNumber
                        aria-label='Maximum word count'
                        min={0}
                        onChange={(value) => handleChange(['maxWords'], value ?? undefined)}
                        placeholder='Maximum...'
                        style={{ width: '100%' }}
                        value={numericValue(filters.maxWords)}
                    />
                </Col>
            </Row>
        </Form.Item>
        <Form.Item label='Published Between' style={{ marginBottom: '0.75rem' }}>
            <Row gutter={[8, 0]}>
                <Col span={12}>
                    <PlatformDatePicker
                        aria-label='Published after'
                        onChange={(value) => handleChange(['publishedFrom'], value?.format('YYYY-MM-DD'))}
                        placeholder='Start Date...'
                        style={{ width: '100%' }}
                        suffixIcon={<TbCalendarEvent />}
                        value={stringValue(filters.publishedFrom) ? dayjs(stringValue(filters.publishedFrom)) : null}
                    />
                </Col>
                <Col span={12}>
                    <PlatformDatePicker
                        aria-label='Published before'
                        onChange={(value) => handleChange(['publishedTo'], value?.format('YYYY-MM-DD'))}
                        placeholder='End Date...'
                        style={{ width: '100%' }}
                        suffixIcon={<TbCalendarEvent />}
                        value={stringValue(filters.publishedTo) ? dayjs(stringValue(filters.publishedTo)) : null}
                    />
                </Col>
            </Row>
        </Form.Item>
        <Form.Item label='File Formats' style={{ marginBottom: '0.75rem' }}>
            <PlatformSelect
                allowClear
                mode='multiple'
                onChange={(value) => handleChange(['formats'], value)}
                options={['pdf', 'docx', 'epub', 'sheet', 'slide', 'text'].map((value) => ({
                    label: value.toUpperCase(),
                    value,
                }))}
                placeholder='Select Formats...'
                style={{ width: '100%' }}
                value={stringArray(filters.formats)}
            />
        </Form.Item>
        <Form.Item label='Revision History' style={{ marginBottom: 0 }}>
            <Checkbox
                checked={filters.hasRevisions === true}
                onChange={(event) => handleChange(['hasRevisions'], event.target.checked)}
            >
                Has revisions
            </Checkbox>
        </Form.Item>
    </Form>
);

export default Metadata;
